(() => {
  const ROOT_SELECTOR = '[data-sc-quick-options]';
  const DIALOG_SELECTOR = '[data-sc-quick-options-dialog]';
  let active = null;

  function initialize(root) {
    if (!root || root.dataset.scQuickOptionsReady === 'true') return;

    const opener = root.querySelector('[data-sc-quick-options-open]');
    const dialog = root.querySelector(DIALOG_SELECTOR);
    if (!opener || !dialog) return;

    const closeButton = dialog.querySelector('[data-sc-quick-options-close]');
    const form = dialog.querySelector('[data-sc-quick-options-form]');
    const variantInput = dialog.querySelector('[data-sc-quick-options-variant]');
    const variantsNode = dialog.querySelector('[data-sc-quick-options-variants]');
    const pricesNode = dialog.querySelector('[data-sc-quick-options-prices]');
    const priceCurrent = dialog.querySelector('[data-sc-quick-options-price-current]');
    const priceCompare = dialog.querySelector('[data-sc-quick-options-price-compare]');
    const addButton = dialog.querySelector('[data-sc-quick-options-add]');
    const addText = dialog.querySelector('[data-sc-quick-options-add-text]');
    const quantity = dialog.querySelector('[data-sc-quick-options-quantity]');
    const inputs = Array.from(dialog.querySelectorAll('[data-sc-quick-options-input]'));

    if (!form || !variantInput || !variantsNode || !addButton) return;

    let variants;
    let prices;
    try {
      variants = JSON.parse(variantsNode.textContent || '[]');
      prices = pricesNode ? JSON.parse(pricesNode.textContent || '[]') : [];
    } catch (error) {
      console.error('SC quick options JSON error:', error);
      return;
    }

    root.dataset.scQuickOptionsReady = 'true';
    document.body.appendChild(dialog);

    const groups = () => Array.from(dialog.querySelectorAll('[data-sc-quick-options-group]'));
    const selectedValues = () => groups().map((group) => group.querySelector('[data-sc-quick-options-input]:checked')?.value || '');
    const findVariant = () => {
      const selected = selectedValues();
      return variants.find((variant) => selected.every((value, index) => variant.options[index] === value));
    };

    function update() {
      const variant = findVariant();
      const available = Boolean(variant?.available);
      variantInput.value = variant?.id || '';
      addButton.disabled = !available;
      if (addText) addText.textContent = available ? 'Add to cart' : 'Sold out';

      const price = prices.find((item) => String(item.id) === String(variant?.id));
      if (priceCurrent) priceCurrent.textContent = price?.price || '';
      if (priceCompare) {
        priceCompare.textContent = price?.compare_at_price || '';
        priceCompare.hidden = !price?.compare_at_price;
      }

      groups().forEach((group) => {
        const checked = group.querySelector('[data-sc-quick-options-input]:checked');
        const output = group.querySelector('[data-sc-quick-options-selected]');
        if (checked && output) output.textContent = checked.value;
      });
    }

    function close(restoreFocus = true) {
      dialog.hidden = true;
      dialog.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sc-quick-options-open');
      if (active?.dialog === dialog) active = null;
      if (restoreFocus && document.contains(opener)) opener.focus();
    }

    function open() {
      if (active && active.dialog !== dialog) active.close(false);
      dialog.hidden = false;
      dialog.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sc-quick-options-open');
      active = { dialog, close };
      requestAnimationFrame(() => closeButton?.focus());
    }

    opener.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      open();
    });
    closeButton?.addEventListener('click', () => close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    inputs.forEach((input) => input.addEventListener('change', update));

    dialog.querySelector('[data-sc-quick-options-minus]')?.addEventListener('click', () => {
      quantity.value = Math.max(1, Number(quantity.value || 1) - 1);
    });
    dialog.querySelector('[data-sc-quick-options-plus]')?.addEventListener('click', () => {
      quantity.value = Math.max(1, Number(quantity.value || 1) + 1);
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (addButton.disabled || !variantInput.value) return;

      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      if (addText) addText.textContent = 'Adding...';

      try {
        const formData = new FormData(form);
        const addUrl = window.routes?.cart_add_url || '/cart/add.js';

        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
              result.message ||
              'Unable to add this item.'
          );
        }

        const cartUrl = window.routes?.cart_url || '/cart';
        const iconResponse = await fetch(
          `${cartUrl}?section_id=cart-icon-bubble`,
          {
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          }
        );

        if (iconResponse.ok) {
          const html = await iconResponse.text();
          const parsed = new DOMParser().parseFromString(
            html,
            'text/html'
          );
          const newBubble = parsed.querySelector(
            '#shopify-section-cart-icon-bubble'
          );
          const currentBubble = document.querySelector(
            '#shopify-section-cart-icon-bubble'
          );

          if (newBubble && currentBubble) {
            currentBubble.innerHTML = newBubble.innerHTML;
          }
        }

        document.dispatchEvent(
          new CustomEvent('sc:cart-added', {
            bubbles: true,
            detail: {
              item: result,
              message: 'Item added to cart',
            },
          })
        );

        close(false);
      } catch (error) {
        console.error('SC quick options:', error);
        if (addText) {
          addText.textContent = error.message || 'Try again';
        }
      } finally {
        addButton.removeAttribute('aria-busy');
        update();
      }
    });

    update();
  }

  function init(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && active) active.close();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
